import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import { AppContext } from '../../../contexts/AppContext';
import useAuthService from '../../../app/services/hooks/useAuthService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import LazyLoading from '../../../components/LazyLoading';
import InputField from '../../../components/InputField';
import logo_monere from '../../../assets/images/logo_monere.png';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import './Login.css';
import { mapAuthResponse } from '../../../app/models/Auth';

const Login = () => {
    const history = useHistory();
    const { login } = useContext(AuthContext);
    const { showLoading, hideLoading } = useContext(AppContext);

    const { fetchingLoginAuth, loginAuth } = useAuthService();

    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
    const [errorMessage, setErrorMessage] = useState('');

    const [credentials, setCredentials] = useState({
        username: '',
        password: '',
        haveSesion: false
    });
    const { navigationPage } = useNavigationPage();

    useEffect(() => {
        setErrorMessage('');
    }, [credentials]);

    const handleSubmit = () => {
        if (showLoading) showLoading('loading', 'Conectando...');
        loginAuth(credentials, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                const { data } = response;

                if (data.sesion && data.sesion === true) {
                    confirmLogin(data.sesion);
                    return;
                }

                if (login) {
                    login(mapAuthResponse(data.auth));

                    // window.location.href = '/';
                    navigationPage('/intranet', false);
                }
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorMessage(response.message);
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrors(response.data);
            }
        });
    };

    const confirmLogin = (sesion = false) => {
        useReactConfirmAlert().requestConfirmation({
            title: 'Hay otra sesión iniciada con esta cuenta en estos momentos',
            message: '',
            customComponent: (
                <div className="text-alert">
                    <p>¿Desea cerrar la sesión para iniciar una nueva?</p>
                </div>
            ),
            buttons: {
                confirmButton: {
                    label: 'Sí, iniciar',
                    onClick: () => {
                        credentials.haveSesion = true;
    
                        if (showLoading) showLoading('loading', 'Iniciando nueva sesión...');
                        handleSubmit();
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'No se ha iniciado una nueva sesión.'
                            });
                        }, 0);
                        credentials.haveSesion = false;
                    }
                }
            },
            onClickOutsideFunction: () => {
                credentials.haveSesion = false;
            }
        });
    };
    
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setCredentials({ ...credentials, [name]: value });
    };

    return (
        <div className="login-container">
            <div className="login-background">
                <div className="login-background-pattern"></div>
            </div>
            
            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <div className="login-logo">
                            <img src={logo_monere} alt="Monere" />
                        </div>
                        <h1 className="login-title">Acceso al Sistema</h1>
                        <p className="login-subtitle">Ingrese sus credenciales para continuar</p>
                    </div>

                    {!fetchingLoginAuth ? (
                        <div className="login-body">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit();
                                }}
                                className="login-form"
                            >
                                <InputField
                                    type="email"
                                    label="Correo electrónico"
                                    value={credentials.username}
                                    onChange={(value) => setCredentials({ ...credentials, username: value })}
                                    onFocus={() => onFocusRemove('username')}
                                    placeholder="Ingrese su correo electrónico"
                                    error={fieldErrorMessage('username')}
                                    icon="fas fa-envelope"
                                    backgroundColor="#FAFBFC"
                                    required
                                />

                                <InputField
                                    type="password"
                                    label="Contraseña"
                                    value={credentials.password}
                                    onChange={(value) => setCredentials({ ...credentials, password: value })}
                                    onFocus={() => onFocusRemove('password')}
                                    placeholder="Ingrese su contraseña"
                                    error={fieldErrorMessage('password')}
                                    icon="fas fa-lock"
                                    backgroundColor="#FAFBFC"
                                    required
                                />

                                <button
                                    type="submit"
                                    className="btn-login"
                                >
                                    <span>Iniciar Sesión</span>
                                    <i className="fas fa-arrow-right"></i>
                                </button>

                                <div className="login-footer">
                                    <button
                                        type="button"
                                        className="btn-link"
                                        onClick={() => (window.location.href = '/recovery-password')}
                                    >
                                        ¿Olvidó su contraseña?
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="login-loading">
                            <LazyLoading height={200} />
                        </div>
                    )}
                </div>

                <div className="login-promo">
                    <div className="promo-content">
                        <h2 className="promo-title">
                            Optimiza la gestión legal de tu empresa
                        </h2>
                        <p className="promo-text">
                            con herramientas digitales, garantizando eficiencia y reduciendo costos y tiempo.
                        </p>
                        <div className="promo-features">
                            <div className="feature-item">
                                <i className="fas fa-shield-alt"></i>
                                <span>Seguridad Avanzada</span>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-clock"></i>
                                <span>Ahorro de Tiempo</span>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-chart-line"></i>
                                <span>Eficiencia Mejorada</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
